import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { dataHoraCurta, podeFazer } from '../../acesso';
import type { Eu, Falha } from '../../acesso';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { listarProdutos, reais, reaisCurto } from '../../produtos';
import { mostraValor, useValoresOcultos } from '../../design/sigilo';
import type {
  FiltroDeProdutos,
  ListaDeProdutos,
  OrdemDosProdutos,
  ProdutoComercial,
} from '../../produtos';
import { ErroDaLista, Esqueleto } from '../equipe/Equipe';
import type { LancadosNoPeriodo } from '../../lancados-no-periodo';
import { DrawerDoProduto } from './DrawerDoProduto';
import '../equipe/acesso.css';
import './produtos.css';

/**
 * TELA 28 — PRODUTOS E CONFIGURAÇÃO COMERCIAL (Bloco 4).
 *
 * "Produto mestre ≠ configuração comercial." A lista mostra o catálogo importado (leitura) e a
 * camada comercial do InovaPay (a vitamina, versionada). Produto sem configuração aparece como
 * ESTADO, não como erro; produto sem PMC mostra "indisponível", nunca R$ 0,00.
 *
 * O que ainda não existe aparece dito: "em campanha" e as condições calculadas (pré-vencido,
 * paradão) chegam com os blocos delas — a coluna mostra "—", nunca uma etiqueta inventada.
 */

type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly dados: ListaDeProdutos };

const ORDENS: readonly (readonly [OrdemDosProdutos, string])[] = [
  ['recentes', 'Configurados primeiro'],
  ['descricao', 'Nome (A–Z)'],
  ['estoque_maior', 'Maior estoque'],
  ['estoque_menor', 'Menor estoque'],
  ['pmc_maior', 'Maior PMC'],
  ['pmc_menor', 'Menor PMC'],
  ['base_maior', 'Maior preço base'],
  ['base_menor', 'Menor preço base'],
  ['custo_maior', 'Maior custo'],
  ['custo_menor', 'Menor custo'],
];

const CHIPS: readonly (readonly [FiltroDeProdutos, string])[] = [
  ['todos', 'Todos'],
  ['ativos', 'Ativos'],
  ['inativos', 'Inativos'],
  ['vitaminas', 'Vitaminas'],
  ['com_premiacao', 'Com premiação'],
  ['pendencias', 'Pendências'],
  ['sem_configuracao', 'Sem configuração'],
];

export function Produtos({
  sessao,
  eu,
  filtroInicial = 'todos',
  parametros,
  lancados,
}: {
  sessao: Sessao;
  eu: Eu | null;
  /** O filtro de período do módulo Vitaminas: quanto de cada um foi lançado nele. */
  lancados?: LancadosNoPeriodo | undefined;
  /** ?produto=<id> abre o produto (vem da busca global); ?busca= preenche a busca. */
  parametros?: URLSearchParams;
  /** "Vitaminas" no menu abre esta tela já no filtro das vitaminas. */
  filtroInicial?: FiltroDeProdutos;
}) {
  const ocultos = useValoresOcultos();
  const [filtro, setFiltro] = useState<FiltroDeProdutos>(filtroInicial);
  const [busca, setBusca] = useState(parametros?.get('busca') ?? '');
  const [buscaAplicada, setBuscaAplicada] = useState(parametros?.get('busca') ?? '');
  const [grupo, setGrupo] = useState('');
  const [classificacao, setClassificacao] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [ordem, setOrdem] = useState<OrdemDosProdutos>('recentes');
  const [comEstoque, setComEstoque] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [aberto, setAberto] = useState<string | null>(() => {
    const id = parametros?.get('produto') ?? null;
    return id && /^[0-9a-f-]{36}$/.test(id) ? id : null;
  });
  const configura = podeFazer(eu, 'produtos.configurar');

  // A busca espera a pessoa parar de digitar: não é uma requisição por tecla.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setBuscaAplicada(busca);
      setPagina(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setEstado((e) => (e.fase === 'pronto' ? e : { fase: 'carregando' }));
    const r = await listarProdutos({
      filtro,
      busca: buscaAplicada,
      grupo,
      classificacao,
      fabricante,
      ordem,
      comEstoque,
      pagina,
      lancados: lancados ?? null,
    });
    setEstado(r.ok ? { fase: 'pronto', dados: r.dados } : { fase: 'erro', falha: r.falha });
  }, [
    filtro,
    buscaAplicada,
    grupo,
    classificacao,
    fabricante,
    ordem,
    comEstoque,
    pagina,
    lancados?.de,
    lancados?.ate,
    lancados?.so,
  ]);
  // Trocou o período: volta para a primeira página.
  useEffect(() => setPagina(1), [lancados?.de, lancados?.ate, lancados?.so]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso')
    return <SemAcesso area="Produtos" />;

  const dados = estado.fase === 'pronto' ? estado.dados : null;
  const c = dados?.contagens;
  const catalogoVazio = dados !== null && dados.contagens.catalogo === 0;
  const pendencias = c ? c.incompletos + c.semPmc : null;
  const ultimaImportacao = maisRecente(dados?.itens ?? []);

  const escolher = (f: FiltroDeProdutos) => {
    setFiltro(f);
    setPagina(1);
  };

  return (
    <div className="ac ac-cheia pd">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">
            {filtroInicial === 'vitaminas'
              ? 'Vitaminas · produtos e faixas'
              : 'Produtos e configuração comercial'}
          </h1>
          <div className="ac-subtitulo">
            {filtroInicial === 'vitaminas'
              ? ocultos
                ? 'Cada vitamina com as faixas A/B e o PMC'
                : 'Cada vitamina com as faixas A/B, o prêmio e o PMC'
              : 'Catálogo do HOS · a camada comercial é do InovaPay'}{' '}
            {ultimaImportacao && (
              <span className="pd-fonte">
                · catálogo do HOS (API) atualizado {dataHoraCurta(ultimaImportacao)}
              </span>
            )}
          </div>
        </div>
        <div className="ac-acoes">
          <label className="ac-busca">
            <Icone nome="search" tamanho={14} traco={1.6} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Descrição, EAN, código interno ou laboratório"
              aria-label="Buscar produtos"
            />
          </label>
          {configura && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() => escolher('sem_configuracao')}
            >
              <Icone nome="plus" tamanho={14} traco={1.8} />
              Configurar produto
            </button>
          )}
        </div>
      </div>

      <div className="ac-kpis">
        <Kpi rotulo="CONFIGURADOS" valor={c?.configurados} sub="ativos no InovaPay" />
        <Kpi rotulo="VITAMINAS" valor={c?.vitaminas} sub="com faixas" />
        <Kpi rotulo="INCOMPLETOS" valor={c?.incompletos} sub="faixa sem premiação" tom="aviso" />
        <Kpi rotulo="SEM CONFIGURAÇÃO" valor={c?.semConfiguracao} sub="no catálogo" tom="neutro" />
      </div>

      <div className="ac-filtros">
        {CHIPS.map(([id, rotulo]) => (
          <button
            key={id}
            type="button"
            aria-pressed={filtro === id}
            className={`ac-chip${id === 'pendencias' && pendencias ? ' pd-chip-aviso' : ''}${filtro === id ? ' ac-chip-ativo' : ''}`}
            onClick={() => escolher(id)}
          >
            {rotulo}
            {id === 'pendencias' && pendencias ? ` ${String(pendencias)}` : ''}
          </button>
        ))}
        <span className="ac-espaco" />
        <label className="ac-seletor">
          <span className="ac-seletor-rotulo">Grupo</span>
          <select
            value={grupo}
            onChange={(e) => {
              setGrupo(e.target.value);
              setPagina(1);
            }}
            aria-label="Grupo"
          >
            <option value="">Todos</option>
            {(dados?.grupos ?? []).map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <label className="ac-seletor">
          <span className="ac-seletor-rotulo">Classificação</span>
          <select
            value={classificacao}
            onChange={(e) => {
              setClassificacao(e.target.value);
              setPagina(1);
            }}
            aria-label="Classificação"
          >
            <option value="">Todas</option>
            {(dados?.classificacoes ?? []).map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <label className="ac-seletor pd-fabricante">
          <span className="ac-seletor-rotulo">Laboratório</span>
          <input
            list="pd-fabricantes"
            value={fabricante}
            placeholder="Todos"
            onChange={(e) => {
              const v = e.target.value;
              setFabricante(v);
              // só filtra quando é um laboratório que existe (ou vazio): não a cada letra
              if (!v || (dados?.fabricantes ?? []).includes(v)) setPagina(1);
            }}
            aria-label="Laboratório / fabricante"
          />
          <datalist id="pd-fabricantes">
            {(dados?.fabricantes ?? []).map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </label>
        <label className="ac-seletor">
          <span className="ac-seletor-rotulo">Ordenar</span>
          <select
            value={ordem}
            onChange={(e) => {
              setOrdem(e.target.value as OrdemDosProdutos);
              setPagina(1);
            }}
            aria-label="Ordenar"
          >
            {ORDENS.map(([id, r]) => (
              <option key={id} value={id}>
                {r}
              </option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <label className="pd-com-estoque">
          <input
            type="checkbox"
            checked={comEstoque}
            onChange={(e) => {
              setComEstoque(e.target.checked);
              setPagina(1);
            }}
          />
          Só com estoque
        </label>
      </div>

      <div className="ac-cartao ac-cartao-tabela">
        {estado.fase === 'carregando' && <Esqueleto linhas={6} />}
        {estado.fase === 'erro' && (
          <div style={{ padding: 16 }}>
            <ErroDaLista
              titulo="Não foi possível carregar os produtos"
              texto="As configurações já carregadas continuam visíveis."
              aoTentar={() => void carregar()}
            />
          </div>
        )}
        {dados && dados.itens.length === 0 && (
          <Vazio
            catalogoVazio={catalogoVazio}
            aoLimpar={() => {
              setFiltro('todos');
              setBusca('');
              setGrupo('');
              setClassificacao('');
              setFabricante('');
              setComEstoque(false);
            }}
          />
        )}
        {dados && dados.itens.length > 0 && (
          <div className="ac-tabela-rolo">
            <table className="ac-tabela pd-tabela">
              <thead>
                <tr>
                  <th>PRODUTO</th>
                  <th className="ac-col-larga">GRUPO · CLASSIFICAÇÃO</th>
                  <th>PROGRAMAS</th>
                  <th className="ac-direita">ESTOQUE</th>
                  {lancados && <th className="ac-direita">LANÇADOS NO PERÍODO</th>}
                  <th className="ac-direita">CUSTO</th>
                  <th className="ac-direita">PREÇO BASE</th>
                  <th className="ac-direita">PMC (HOS)</th>
                  {!ocultos && <th className="ac-direita">PREMIAÇÃO VITAMINA</th>}
                  <th>STATUS</th>
                  <th className="ac-col-larga">ÚLTIMA ALTERAÇÃO</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dados.itens.map((p) => (
                  <Linha
                    key={p.id}
                    p={p}
                    aoAbrir={() => setAberto(p.id)}
                    configura={configura}
                    comLancados={!!lancados}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ac-rodape-tabela">
          <span>
            {dados
              ? dados.total === 0
                ? '0 produtos'
                : `${String((dados.pagina - 1) * dados.tamanhoDaPagina + 1)}–${String(
                    (dados.pagina - 1) * dados.tamanhoDaPagina + dados.itens.length,
                  )} de ${dados.total.toLocaleString('pt-BR')}`
              : ' '}
            {dados && dados.total > dados.tamanhoDaPagina && (
              <span className="pd-paginas">
                <button
                  type="button"
                  className="ac-link"
                  disabled={dados.pagina <= 1}
                  onClick={() => setPagina((n) => Math.max(1, n - 1))}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="ac-link"
                  disabled={dados.pagina * dados.tamanhoDaPagina >= dados.total}
                  onClick={() => setPagina((n) => n + 1)}
                >
                  Próxima
                </button>
              </span>
            )}
          </span>
          <span>
            Programa é configuração; condições (pré-vencido, paradão, campanha) são calculadas pelo
            servidor · sem PMC é <b>indisponível</b>, nunca R$ 0,00
          </span>
        </div>
      </div>

      {/* Celular (28-C): consulta; editar várias faixas é mais seguro no desktop. */}
      <div className="ac-cards">
        {estado.fase === 'carregando' && (
          <div className="ac-cartao">
            <Esqueleto />
          </div>
        )}
        {estado.fase === 'erro' && (
          <ErroDaLista
            titulo="Não foi possível carregar os produtos"
            aoTentar={() => void carregar()}
          />
        )}
        {dados && dados.itens.length === 0 && (
          <div className="ac-cartao">
            <Vazio catalogoVazio={catalogoVazio} aoLimpar={() => setFiltro('todos')} />
          </div>
        )}
        {dados?.itens.map((p) => (
          <CardDoProduto key={p.id} p={p} aoAbrir={() => setAberto(p.id)} configura={configura} />
        ))}
      </div>

      {aberto && (
        <DrawerDoProduto
          id={aberto}
          sessao={sessao}
          configura={configura}
          aoFechar={() => setAberto(null)}
          aoSalvar={() => void carregar()}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export function statusDoProduto(p: ProdutoComercial): { rotulo: string; classe: string } {
  if (!p.vitamina) return { rotulo: 'Sem configuração de vitamina', classe: 'ac-status-inativo' };
  if (!p.vitamina.ativa) return { rotulo: 'Inativa', classe: 'ac-status-inativo' };
  if (p.pendencias.includes('sem_pmc'))
    return { rotulo: 'PMC pendente', classe: 'ac-status-pendente' };
  if (p.pendencias.includes('faixa_sem_premio'))
    return { rotulo: 'Incompleta', classe: 'ac-status-suspenso' };
  return { rotulo: 'Ativa', classe: 'ac-status-ativo' };
}

function Linha({
  p,
  aoAbrir,
  configura,
  comLancados,
}: {
  p: ProdutoComercial;
  aoAbrir: () => void;
  configura: boolean;
  comLancados: boolean;
}) {
  const ocultos = useValoresOcultos();
  const v = p.vitamina;
  const st = statusDoProduto(p);
  // premio vem null no "só números": não é "faixa sem valor"
  const semValor = ocultos ? undefined : v?.faixas.find((f) => Number(f.premio) === 0);
  const apagado = v !== null && !v.ativa;
  return (
    <tr className="pd-linha" onClick={aoAbrir}>
      <td>
        <div className="ac-pessoa-nome">{p.descricao}</div>
        <div className="pd-codigos">
          {[p.codigo, p.ean, p.fabricante].filter(Boolean).join(' · ') || 'sem código no catálogo'}
        </div>
      </td>
      <td className="ac-cinza ac-col-larga">
        {p.grupo ?? '—'}
        <div className="pd-codigos">{p.classificacao ?? ''}</div>
      </td>
      <td>
        <Programas p={p} />
      </td>
      <td className="ac-direita ac-num">{p.estoque ?? '—'}</td>
      {comLancados && (
        <td className="ac-direita ac-num">
          {p.lancadosNoPeriodo ? <strong>{p.lancadosNoPeriodo}</strong> : '0'}
        </td>
      )}
      <td className="ac-direita ac-num">{p.custo ? reais(p.custo) : '—'}</td>
      <td className="ac-direita ac-num">{p.precoBase ? reais(p.precoBase) : '—'}</td>
      <td className="ac-direita ac-num">
        {p.pmc === null ? <span className="pd-indisponivel">indisponível</span> : reais(p.pmc)}
      </td>
      {!ocultos && (
        <td className="ac-direita ac-num">
          {!v ? (
            <span className="pd-nota">sem vitamina</span>
          ) : semValor && v.ativa ? (
            <span className="pd-aviso-txt">faixa {semValor.ordem} sem valor</span>
          ) : (
            <b className={apagado ? 'pd-apagado' : undefined}>
              {v.faixas.map((f) => reaisCurto(f.premio)).join(' · ')}
            </b>
          )}
        </td>
      )}
      <td>
        <span className={`ac-status ${st.classe}`}>
          {st.rotulo === 'Incompleta' && <Icone nome="warn" tamanho={11} traco={1.8} />}
          {st.rotulo}
        </span>
      </td>
      <td className="ac-cinza ac-col-larga">
        {v ? `${v.criadoPor ?? 'migração do 1.0'} · ${dataHoraCurta(v.criadoEm)}` : '—'}
      </td>
      <td className="ac-direita">
        <button type="button" className="ac-link" onClick={aoAbrir}>
          {!v && configura ? 'Configurar' : 'Abrir'}
        </button>
      </td>
    </tr>
  );
}

/** Os programas do produto: vitamina (configurada), paradão (na lista) e pré-vencido (lotes). */
function Programas({ p }: { p: ProdutoComercial }) {
  const v = p.vitamina;
  const itens = [
    v ? (v.ativa ? 'Vitamina' : 'Vitamina · encerrada') : null,
    p.noParadao ? 'Paradão' : null,
    p.lotesAtivos > 0
      ? `Pré-vencido · ${String(p.lotesAtivos)} lote${p.lotesAtivos > 1 ? 's' : ''}`
      : null,
  ].filter(Boolean);
  return itens.length ? (
    <div className="pd-programas">
      {itens.map((t) => (
        <span key={t} className="pd-programa">
          {t}
        </span>
      ))}
    </div>
  ) : (
    <span className="pd-traco">—</span>
  );
}

function CardDoProduto({
  p,
  aoAbrir,
  configura,
}: {
  p: ProdutoComercial;
  aoAbrir: () => void;
  configura: boolean;
}) {
  const ocultos = useValoresOcultos();
  const st = statusDoProduto(p);
  const v = p.vitamina;
  return (
    <div className={`ac-card${st.rotulo === 'Incompleta' ? ' pd-card-aviso' : ''}`}>
      <div className="ac-card-linha">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ac-card-nome">{p.descricao}</div>
          <div className="ac-card-sub">
            {[p.codigo, p.fabricante, p.classificacao].filter(Boolean).join(' · ')}
          </div>
          <div className="ac-card-sub">
            estoque {p.estoque ?? '—'} · PMC {p.pmc ? reais(p.pmc) : '—'} · base{' '}
            {p.precoBase ? reais(p.precoBase) : '—'}
            {p.lancadosNoPeriodo !== undefined && ` · ${String(p.lancadosNoPeriodo)} lançados`}
          </div>
          <Programas p={p} />
        </div>
        <span className={`ac-status ${st.classe}`}>
          {st.rotulo === 'Sem configuração de vitamina' ? 'Sem configuração' : st.rotulo}
        </span>
      </div>
      {v && v.ativa && st.rotulo !== 'Incompleta' && (
        <>
          <div className="pd-card-faixas">
            {v.faixas.map((f) => (
              <div key={f.ordem}>
                <div className="pd-card-faixa-rotulo">FAIXA {f.ordem}</div>
                <div className="pd-card-faixa-valor">
                  {reais(f.precoMinimo)}
                  {mostraValor(ocultos, f.premio) && ` · ${reaisCurto(f.premio)}`}
                </div>
              </div>
            ))}
          </div>
          <div className="pd-card-pe">
            PMC {p.pmc ? reais(p.pmc) : 'indisponível'} · alterado por {v.criadoPor ?? 'migração'}{' '}
            em {dataHoraCurta(v.criadoEm).slice(0, 5)}
          </div>
        </>
      )}
      {st.rotulo === 'Incompleta' && (
        <div className="pd-card-texto">
          Uma faixa está sem valor de premiação. Editar faixas é mais seguro no desktop.
        </div>
      )}
      {!v && (
        <div className="pd-card-texto">Existe no catálogo, sem camada comercial de vitamina.</div>
      )}
      <div className="pd-card-acoes">
        <button type="button" className="ac-link" onClick={aoAbrir}>
          {!v && configura ? 'Configurar' : 'Abrir'}
        </button>
      </div>
    </div>
  );
}

function Kpi({
  rotulo,
  valor,
  sub,
  tom,
}: {
  rotulo: string;
  valor: number | null | undefined;
  sub: string;
  tom?: 'aviso' | 'alerta' | 'neutro';
}) {
  const ativo = valor !== null && valor !== undefined && valor > 0;
  return (
    <div
      className={`ac-kpi pd-kpi${ativo && tom === 'aviso' ? ' ac-kpi-aviso' : ''}${ativo && tom === 'alerta' ? ' ac-kpi-alerta' : ''}`}
    >
      <div className="ac-kpi-rotulo">{rotulo}</div>
      <div className={`ac-kpi-valor${tom === 'neutro' ? ' pd-kpi-neutro' : ''}`}>
        {valor === null || valor === undefined ? '—' : valor.toLocaleString('pt-BR')}
      </div>
      <div className="pd-kpi-sub">{sub}</div>
    </div>
  );
}

function Vazio({ catalogoVazio, aoLimpar }: { catalogoVazio: boolean; aoLimpar: () => void }) {
  return (
    <div className="ac-vazio">
      <div className="ac-vazio-titulo">
        {catalogoVazio
          ? 'Nenhum produto no catálogo ainda'
          : 'Nenhum produto corresponde aos filtros'}
      </div>
      <div className="ac-vazio-texto">
        {catalogoVazio
          ? 'O catálogo chega pela importação do relatório de produtos do HOS. Nada aqui é inventado.'
          : 'Troque o filtro, o grupo ou a busca.'}
      </div>
      {!catalogoVazio && (
        <button type="button" className="ac-botao" style={{ marginTop: 10 }} onClick={aoLimpar}>
          Limpar filtros
        </button>
      )}
    </div>
  );
}

function maisRecente(itens: readonly ProdutoComercial[]): string | null {
  return itens.reduce<string | null>(
    (m, p) => (p.vistoNaOrigemEm && (!m || p.vistoNaOrigemEm > m) ? p.vistoNaOrigemEm : m),
    null,
  );
}
